import { ApolloClient, Operation, InMemoryCache, ApolloLink, from, split, Observable } from '@apollo/client';
import { RetryLink } from '@apollo/client/link/retry';
import { loadErrorMessages, loadDevMessages } from '@apollo/client/dev';
import { onError } from '@apollo/client/link/error';
import { getMainDefinition } from '@apollo/client/utilities';
import { persistCache, LocalStorageWrapper } from 'apollo3-cache-persist';
import { createUploadLink } from 'apollo-upload-client';
import { GraphQLJSON } from 'graphql-type-json';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient, CloseCode } from 'graphql-ws';
import { Capacitor } from '@capacitor/core';

import { RefreshTokensDocument } from '@graphql';
import { secureGetItem, secureSetItem, secureRemoveItem, getDeviceId } from '@utils';
import { version } from '../../package.json';
import { loginStatusVar } from '../context';
import typePolicies from './typePolicies';
import * as links from './links';

const cache = new InMemoryCache(typePolicies);

let refreshingTokenPromise: Promise<string | null> | null = null;

// flag used only for websocket auth flow
let shouldRefreshWsToken = false;

if (import.meta.env.DEV && !Capacitor.isNativePlatform()) {
  loadDevMessages();
  loadErrorMessages();
}

import.meta.env.DEV &&
  console.log(`[ENDPOINTS]:`, {
    VITE_API_URI: import.meta.env.VITE_API_URI,
    VITE_WS_URI: import.meta.env.VITE_WS_URI,
  });

(async () => {
  await persistCache({
    cache,
    storage: new LocalStorageWrapper(window.localStorage),
    trigger: 'write',
    serialize: true,
    debug: import.meta.env.DEV,
    key: 'festayre-cache',
    maxSize: 5000000,
  });
})();

const uploadLink = createUploadLink({
  uri: import.meta.env.VITE_API_URI,
  useGETForQueries: false,
  headers: {
    'apollo-require-preflight': 'true',
  },
  credentials: 'include',
});

const graphqlWsClient = createClient({
  url: import.meta.env.VITE_WS_URI,
  lazy: import.meta.env.DEV ? false : true,
  connectionParams: async () => {
    // If previous WS close told us token is invalid, refresh it before reconnect
    if (Capacitor.isNativePlatform() && shouldRefreshWsToken) {
      import.meta.env.DEV && console.log('[WS] refreshing token before reconnect');
      await refreshAccessTokenApollo();
      shouldRefreshWsToken = false;
    }

    return {
      accessToken: await secureGetItem('accessToken'),
      // do not send refresh token over websockets
      deviceId: await getDeviceId(),
    };
  },
  retryAttempts: 5,
  shouldRetry: (errorOrCloseEvent: unknown) => {
    import.meta.env.DEV && console.error('GraphQL WebSocket error:', errorOrCloseEvent);
    const event = errorOrCloseEvent as { code?: number };
    return import.meta.env.DEV || event.code !== 1006;
  },
  keepAlive: 10000,
  retryWait: (retries: number) => {
    const delay = Math.min(1000 * Math.pow(2, retries), 30000);
    return new Promise((resolve) => setTimeout(resolve, delay));
  },
  on: {
    connecting: () => import.meta.env.DEV && console.log('WebSocket connecting'),
    connected: () => import.meta.env.DEV && console.log('WebSocket connected'),
    closed: (event) => {
      import.meta.env.DEV && console.log('WebSocket closed', event);

      // If server closes with Forbidden, token is no longer valid
      if ((event as CloseEvent).code === CloseCode.Forbidden) {
        import.meta.env.DEV && console.log('[WS] close Forbidden -> will refresh token on next connection');
        shouldRefreshWsToken = true;
      }
    },
    error: (err: any) => import.meta.env.DEV && console.log('WebSocket error', err),
  },
});

const wsLink = new GraphQLWsLink(graphqlWsClient);

const authLink = new ApolloLink((operation, forward) => {
  return new Observable((observer) => {
    (async () => {
      try {
        await setAuthHeader(operation);

        const definition = getMainDefinition(operation.query);
        const isSubscription =
          definition.kind === 'OperationDefinition' && definition.operation === 'subscription';

        let subscriber = forward(operation).subscribe({
          next: observer.next.bind(observer),
          error: async (error) => {
            console.log(`[{error}]:`, error);
            const REFRESH_ERROR_KEYS = ['ACCESS_EXPIRED', 'UNAUTHENTICATED'];
            const INVALID_REFRESH_KEYS = ['INVALID_REFRESH_TOKEN', 'REFRESH_EXPIRED', 'REFRESH_NOT_FOUND'];
            const SESSION_EXPIRED_KEYS = ['INVALID_TOKEN', 'SESSION_EXPIRED'];

            // si l'erreur est vide ou n'est pas un "auth" concerné, on la passe simplement
            const responseErrors = error?.result?.errors ?? error?.graphQLErrors ?? [];

            const shouldRefresh = responseErrors.some(
              (err: any) =>
                REFRESH_ERROR_KEYS.includes(err.message) || REFRESH_ERROR_KEYS.includes(err.extensions?.code)
            );

            const refreshErrors = responseErrors.some((err: any) =>
              INVALID_REFRESH_KEYS.includes(err.message)
            );

            const sessionErrors = responseErrors.some((err: any) =>
              SESSION_EXPIRED_KEYS.includes(err.message)
            );

            const isHard401 = !shouldRefresh && !refreshErrors && !sessionErrors && error?.statusCode === 401;

            const isSilentlyIgnorable =
              !shouldRefresh &&
              !refreshErrors &&
              !sessionErrors &&
              !isHard401 &&
              (responseErrors.length === 0 || Object.keys(error).length === 0);

            import.meta.env.DEV &&
              console.log(`[{AUTH_LINK}]:`, {
                shouldRefresh,
                refreshErrors,
                sessionErrors,
                isHard401,
                isSilentlyIgnorable,
              });

            if (
              import.meta.env.DEV &&
              !shouldRefresh &&
              !refreshErrors &&
              !sessionErrors &&
              !isHard401 &&
              !isSilentlyIgnorable
            ) {
              console.error('[authLink]: Unhandled auth error case.', { error });
            }

            // handled by retryLink or wsLink, no need to do anything
            // if (isSilentlyIgnorable) {
            //   forward(operation).subscribe(observer);
            //   return;
            // }

            if (shouldRefresh && !isSubscription) {
              const newAccessToken = await refreshAccessTokenApollo();

              if (newAccessToken) {
                await setAuthHeader(operation);
                forward(operation).subscribe(observer);
                return;
              } else {
                import.meta.env.DEV && console.warn('[authLink]: No new access token received.');
                await handleRefreshTokenFailure();
              }
            }

            if (refreshErrors || sessionErrors || isHard401) {
              // remove tokens and log out user
              import.meta.env.DEV && console.warn('[authLink]: Refresh token invalid or session expired.');
              await handleRefreshTokenFailure();
            }

            observer.error(error);
          },
          complete: observer.complete.bind(observer),
        });

        return () => subscriber.unsubscribe();
      } catch (err) {
        observer.error(err);
      }
    })();
  });
});

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  if (networkError) {
    import.meta.env.DEV && console.error(`[networkError]:`, networkError);
    return;
  }

  if (graphQLErrors?.length) {
    graphQLErrors.forEach(({ message, locations, path, extensions }) => {
      import.meta.env.DEV &&
        console.error('[GraphQL error]:', {
          message,
          locations,
          path,
          extensions,
          operationName: operation.operationName,
        });
    });
  }
});

// Utiliser split pour diriger les requêtes en fonction de leur type.
const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
  },
  wsLink,
  from([
    links.debugLink, // debugLink must be first to log all operations before any potential modification by other links
    links.retryLink, // retryLink must be before the terminating link so it can observe network failures and decide whether to retry
    errorLink, // handle errors
    authLink, // handles authentication
    links.networkRecoveryLink, // resets network error state on any successful operation
    uploadLink, // handles file uploads
  ])
);

const refreshHeadersLink = new ApolloLink((operation, forward) => {
  return new Observable((observer) => {
    (async () => {
      try {
        const deviceId = await getDeviceId();

        operation.setContext(({ headers = {} }) => {
          const nextHeaders: Record<string, string> = { ...headers };

          if (deviceId) {
            nextHeaders['x-device-id'] = deviceId;
          } else {
            delete nextHeaders['x-device-id'];
          }

          return { headers: nextHeaders };
        });

        const subscriber = forward(operation).subscribe({
          next: observer.next.bind(observer),
          error: observer.error.bind(observer),
          complete: observer.complete.bind(observer),
        });

        return () => subscriber.unsubscribe();
      } catch (err) {
        observer.error(err);
      }
    })();
  });
});

const client = new ApolloClient({
  name: 'Festayre',
  version: version,
  credentials: Capacitor.isNativePlatform() ? undefined : 'include',
  resolvers: {
    JSON: GraphQLJSON,
  },
  cache,
  link: splitLink,
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
    },
  },
});

const refreshClient = new ApolloClient({
  name: 'Festayre',
  version: version,
  link: from([links.retryLink, refreshHeadersLink, uploadLink]),
  cache: new InMemoryCache(), // pas besoin de typePolicies ici
  defaultOptions: {
    mutate: { fetchPolicy: 'no-cache' },
  },
});

async function refreshAccessTokenApollo() {
  if (!Capacitor.isNativePlatform()) {
    return null;
  }

  if (refreshingTokenPromise) {
    // If a refresh is already in progress, wait for it
    return refreshingTokenPromise;
  }

  refreshingTokenPromise = (async () => {
    try {
      let refreshToken = await secureGetItem('refreshToken');

      if (!refreshToken) {
        import.meta.env.DEV && console.warn('[refreshAccessTokenApollo]: No refresh token found.');
        refreshingTokenPromise = null;
        return null;
      }

      const { data } = await refreshClient.mutate({
        mutation: RefreshTokensDocument,
        variables: { refreshToken },
        fetchPolicy: 'no-cache',
      });

      const tokens = data?.refreshTokens;
      const accessToken = tokens?.access;
      const newRefreshToken = tokens?.refresh;

      if (accessToken && newRefreshToken) {
        await secureSetItem('accessToken', accessToken);
        await secureSetItem('refreshToken', newRefreshToken);
        return accessToken;
      }

      import.meta.env.DEV && console.warn('[refreshAccessTokenApollo]: No access token returned.');

      return null;
    } catch (error) {
      import.meta.env.DEV && console.error('[refreshAccessTokenApollo]: Error', error);
      await handleRefreshTokenFailure();
      return null;
    } finally {
      refreshingTokenPromise = null; // Always clear the lock
    }
  })();

  return refreshingTokenPromise;
}

function forceWsReconnect() {
  if (graphqlWsClient) {
    graphqlWsClient.terminate();
    import.meta.env.DEV && console.log('🔄 WebSocket manually terminated to force reconnect.');
  }
}

async function setAuthHeader(operation: Operation) {
  const isNative = Capacitor.isNativePlatform();

  const accessToken = isNative ? await secureGetItem('accessToken') : null;
  const deviceId = await getDeviceId();

  operation.setContext(({ headers = {} }) => {
    const nextHeaders: Record<string, string> = { ...headers };

    if (accessToken) {
      nextHeaders.Authorization = `Bearer ${accessToken}`;
    } else {
      delete nextHeaders.Authorization;
    }

    if (deviceId) {
      nextHeaders['x-device-id'] = deviceId;
    } else {
      delete nextHeaders['x-device-id'];
    }

    return { headers: nextHeaders };
  });
}

async function handleRefreshTokenFailure() {
  await secureRemoveItem('accessToken');
  await secureRemoveItem('refreshToken');

  loginStatusVar({
    isLoggedIn: false,
    isLogging: false,
    forceGetSession: true,
    mustFinalizeRegister: false,
    isSessionExpired: true,
    shouldLogFirst: false,
    redirection: null,
  });

  cache.reset();
}

export async function onNativeLoginTokensUpdated() {
  if (!Capacitor.isNativePlatform()) return;

  // Force WS to reconnect with fresh connectionParams(accessToken)
  forceWsReconnect();

  // Ensure HTTP queries re-run with Authorization header
  await client.resetStore();
}

export default client;
